import {
  getVisibleWorkflowQuestions,
  getWorkflowForCodeType,
  mergeWithSystemAttributes
} from '@codametrix/shared-views';

/**
 * Gets all spans from invoices and loose spans.
 * This mirrors the logic in selectSpanAnnotations but without requiring a selector.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {Accelerate.SpanAnnotation[]} - All spans from codes and loose spans.
 */
const getAllSpansFromState = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): Accelerate.SpanAnnotation[] => {
  const invoices = gradeBenchPanelState.invoices || [];
  const looseSpans = gradeBenchPanelState.looseSpans || [];

  // Extract spans from all codes in invoices
  const codeSpans: Accelerate.SpanAnnotation[] = [];
  invoices.forEach(invoice => {
    invoice.transactions?.forEach(transaction => {
      transaction.procedures?.forEach(proc => {
        if (proc.spans) codeSpans.push(...proc.spans);
      });
      transaction.diagnoses?.forEach(diag => {
        if (diag.spans) codeSpans.push(...diag.spans);
      });
      transaction.modifiers?.forEach(mod => {
        if (mod.spans) codeSpans.push(...mod.spans);
      });
    });
  });

  // Combine code spans and loose spans, deduplicating by ID
  const seenIds = new Set<number>();
  const allSpans: Accelerate.SpanAnnotation[] = [];

  [...codeSpans, ...looseSpans].forEach(span => {
    if (!seenIds.has(span.id)) {
      seenIds.add(span.id);
      allSpans.push(span);
    }
  });

  return allSpans;
};

/**
 * Checks if a rubric is assigned to the project.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {boolean} - True if a rubric is assigned.
 */
const hasRubricAssigned = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const rubricVersionId =
    gradeBenchPanelState.qaRule?.data?.project_configuration?.rubricVersionId;
  return rubricVersionId !== null && rubricVersionId !== undefined;
};

/**
 * Checks if all required visible questions for a code have been answered.
 *
 * @param {Accelerate.CQACode} code - The code to check.
 * @param {CQA.WorkflowQuestion[] | undefined} workflowQuestions - Workflow questions for this code type.
 * @returns {boolean} - True if all required visible questions have answers.
 */
const areRequiredQuestionsAnswered = (
  code: Accelerate.CQACode,
  workflowQuestions: CQA.WorkflowQuestion[] | undefined
): boolean => {
  // If no workflow questions, consider it satisfied (code from scratch case)
  if (!workflowQuestions || workflowQuestions.length === 0) {
    return true;
  }

  const codeAttributes = code.attributes || [];

  // Merge with system question attributes for workflow calculations
  const allAttributes = mergeWithSystemAttributes(
    codeAttributes,
    workflowQuestions,
    code.original
  );

  // Get visible questions based on current answers and triggers
  const visibleQuestions = getVisibleWorkflowQuestions(
    workflowQuestions,
    allAttributes
  );

  // Filter to only required questions
  const requiredQuestions = visibleQuestions.filter(
    (wq: CQA.WorkflowQuestion) => wq.question.required
  );

  // Check if all required questions have answers
  return requiredQuestions.every((wq: CQA.WorkflowQuestion) =>
    codeAttributes.some(attr => attr.questionId === wq.question.id)
  );
};

/**
 * Checks if all required visible case-level questions have been answered.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {boolean} - True if all required case questions have answers, or if no case workflow exists.
 */
const areRequiredCaseQuestionsAnswered = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const rubricVersion = gradeBenchPanelState.rubricVersion;
  const caseAttributes = gradeBenchPanelState.caseAttributes || [];

  if (!rubricVersion) {
    return true;
  }

  const caseWorkflow = getWorkflowForCodeType(rubricVersion, 'case');

  // If no case workflow, consider it satisfied
  if (!caseWorkflow?.questions || caseWorkflow.questions.length === 0) {
    return true;
  }

  const visibleQuestions = getVisibleWorkflowQuestions(
    caseWorkflow.questions,
    caseAttributes
  );

  const requiredQuestions = visibleQuestions.filter(
    (wq: CQA.WorkflowQuestion) => wq.question.required
  );

  return requiredQuestions.every((wq: CQA.WorkflowQuestion) =>
    caseAttributes.some(attr => attr.questionId === wq.question.id)
  );
};

/**
 * Checks if all required visible span-level questions have been answered for all spans.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {boolean} - True if all required span questions have answers for all spans, or if no span workflow exists.
 */
const areRequiredSpanQuestionsAnswered = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const rubricVersion = gradeBenchPanelState.rubricVersion;

  if (!rubricVersion) {
    return true;
  }

  const spanWorkflow = getWorkflowForCodeType(rubricVersion, 'span');

  // If no span workflow, consider it satisfied
  if (!spanWorkflow?.questions || spanWorkflow.questions.length === 0) {
    return true;
  }

  const allSpans = getAllSpansFromState(gradeBenchPanelState);

  // If no spans exist, nothing to validate
  if (allSpans.length === 0) {
    return true;
  }

  // Check each span has all required questions answered
  return allSpans.every(span => {
    const spanAttributes = span.rubricAttributes || [];

    // Get visible questions based on current answers
    // Note: Spans don't have an 'original' property, so no system question merging
    const visibleQuestions = getVisibleWorkflowQuestions(
      spanWorkflow.questions,
      spanAttributes
    );

    // Filter to only required questions
    const requiredQuestions = visibleQuestions.filter(
      (wq: CQA.WorkflowQuestion) => wq.question.required
    );

    // Check if all required questions have answers
    return requiredQuestions.every((wq: CQA.WorkflowQuestion) =>
      spanAttributes.some(attr => attr.questionId === wq.question.id)
    );
  });
};

/**
 * Determines if all CQA work items in the given grade bench panel state are evaluated.
 * Uses the project configuration to determine which codes and assessment fields are required.
 *
 * When a rubric is assigned:
 * - Validates that all required rubric questions are answered (instead of role/assessment)
 *
 * When no rubric is assigned:
 * - Uses legacy validation checking role and assessment fields
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The state of the grade bench panel containing QA rules and invoices.
 * @returns {boolean} - Returns true if all work items are evaluated, otherwise false.
 */
export const isCQAWorkItemEvaluated = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const projectConfiguration =
    gradeBenchPanelState.qaRule.data.project_configuration;
  const invoices = gradeBenchPanelState.invoices;
  const rubricVersion = gradeBenchPanelState.rubricVersion;
  const rubricAssigned = hasRubricAssigned(gradeBenchPanelState);

  // If rubric is assigned but attributes haven't been fetched yet, wait
  if (rubricAssigned && !gradeBenchPanelState.attributesFetched) {
    return false;
  }

  // Check case-level required questions if rubric is assigned
  if (rubricAssigned && !areRequiredCaseQuestionsAnswered(gradeBenchPanelState)) {
    return false;
  }

  // Check span-level required questions if rubric is assigned
  if (rubricAssigned && !areRequiredSpanQuestionsAnswered(gradeBenchPanelState)) {
    return false;
  }

  /**
   * Checks if a given code is evaluated.
   * For rubric-based validation: checks required workflow questions.
   * For legacy validation: checks assessment and role fields.
   *
   * @param {Accelerate.CQACode} code - The code to evaluate.
   * @param {string} codeType - The type of code ('procedure', 'diagnosis', 'modifier').
   * @returns {boolean} - Returns true if the code is evaluated, otherwise false.
   */
  const isCodeEvaluated = (
    code: Accelerate.CQACode,
    codeType: string
  ): boolean => {
    if (rubricAssigned) {
      // Rubric-based validation: check required questions
      const workflow = getWorkflowForCodeType(rubricVersion, codeType);
      return areRequiredQuestionsAnswered(code, workflow?.questions);
    }

    // Legacy behavior: check assessment and role
    if (projectConfiguration.assessment && !code.codeAssessment?.assessment) {
      return false;
    }
    if (projectConfiguration.role && !code.codeAssessment?.role) {
      return false;
    }
    return true;
  };

  /**
   * Checks if all codes in the given array are evaluated.
   *
   * @param {Accelerate.CQACode[]} codes - The array of codes to evaluate.
   * @param {string} codeType - The type of codes.
   * @returns {boolean} - Returns true if all codes are evaluated, otherwise false.
   */
  const checkIfCodesEvaluated = (
    codes: Accelerate.CQACode[],
    codeType: string
  ): boolean => {
    return codes.every(c => isCodeEvaluated(c, codeType));
  };

  return invoices.every(invoice =>
    invoice.transactions.every(transaction => {
      if (
        projectConfiguration['procedure'] &&
        !checkIfCodesEvaluated(transaction.procedures, 'procedure')
      ) {
        return false;
      }
      if (
        projectConfiguration['diagnosis'] &&
        !checkIfCodesEvaluated(transaction.diagnoses, 'diagnosis')
      ) {
        return false;
      }
      if (
        projectConfiguration['modifier'] &&
        !checkIfCodesEvaluated(transaction.modifiers, 'modifier')
      ) {
        return false;
      }
      return true;
    })
  );
};
